import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// THUNKS
// Ottiene la lista di tutte le interazioni con i clienti
export const getCustomerInteractionsList = createAsyncThunk(
    'customerInteractions/getCustomerInteractionsList',
    async ({ accountId, token, search, start, length, customerId, order }, { rejectWithValue }) => {
    try {
        let url = `${import.meta.env.VITE_API_BASE_URL}/customerInteractions/list?accountId=${accountId}&start=${start}&length=${length}&draw=1&search[value]=`;
        
        // Aggiungo i parametri di ricerca se presenti
        if (search && search.length > 0) {
            url = `${import.meta.env.VITE_API_BASE_URL}/customerInteractions/list?accountId=${accountId}&start=${start}&length=${length}&draw=1&search[value]=${encodeURIComponent(search)}`;
        }
        
        // Aggiungo il customerId se presente per filtrare per cliente specifico
        if (customerId) {
            url += `&customerId=${customerId}`;
        }
        
        // Aggiungo il parametro di ordinamento se presente
        if (order) {
            url += `&order=${encodeURIComponent(JSON.stringify(order))}`;
        }
        
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { method: 'GET', headers };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nel caricamento delle interazioni.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Ottiene i dettagli di una interazione specifica
export const getCustomerInteractionDetails = createAsyncThunk(
    'customerInteractions/getCustomerInteractionDetails',
    async ({ interactionId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/customerInteractions?customerInteractionId=${interactionId}`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { method: 'GET', headers };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nel caricamento dei dettagli dell'interazione.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Crea una nuova interazione
export const createCustomerInteraction = createAsyncThunk(
    'customerInteractions/createCustomerInteraction',
    async ({ customerInteraction, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/customerInteractions`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'POST', 
            headers, 
            body: JSON.stringify({ customerInteraction })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nella creazione dell'interazione.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Aggiorna un'interazione esistente
export const updateCustomerInteraction = createAsyncThunk(
    'customerInteractions/updateCustomerInteraction',
    async ({ customerInteraction, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/customerInteractions`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'PATCH', 
            headers, 
            body: JSON.stringify({ customerInteraction })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nell'aggiornamento dell'interazione.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

// Elimina un'interazione
export const deleteCustomerInteraction = createAsyncThunk(
    'customerInteractions/deleteCustomerInteraction',
    async ({ customerInteractionId, token }, { rejectWithValue }) => {
    try {
        const url = `${import.meta.env.VITE_API_BASE_URL}/customerInteractions`;
        const headers = {
            'Content-Type': 'application/json',
            'auth-token': token
        };
        const options = { 
            method: 'DELETE', 
            headers, 
            body: JSON.stringify({ customerInteractionId })
        };
        
        const response = await fetch(url, options);
        
        if (!response.ok) {
            const error = await response.json();
            return rejectWithValue({
                message: error.message || `Errore sconosciuto nell'eliminazione dell'interazione.`
            });
        }
        const result = await response.json();
        return result;
    } catch (error) {
        return rejectWithValue({
            message: (error instanceof Error) ? error.message : 'Errore di rete. Riprova più tardi.'
        });
    }
});

const initialState = {
    data: null,
    interactionDetails: null,
    error: null,
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false
};

const customerInteractionsSlice = createSlice({
    name: 'customerInteractions',
    initialState,
    reducers: {
        clearCustomerInteractionsData: (state) => {
            state.data = null;
            state.interactionDetails = null;
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // getCustomerInteractionsList
            .addCase(getCustomerInteractionsList.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getCustomerInteractionsList.fulfilled, (state, action) => {
                state.data = action.payload;
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getCustomerInteractionsList.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nel caricamento delle interazioni.";
                state.isLoading = false;
            })
            // getCustomerInteractionDetails
            .addCase(getCustomerInteractionDetails.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getCustomerInteractionDetails.fulfilled, (state, action) => {
                state.interactionDetails = action.payload;
                state.error = null;
                state.isLoading = false;
            })
            .addCase(getCustomerInteractionDetails.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nel caricamento dei dettagli dell'interazione.";
                state.isLoading = false;
            })
            // createCustomerInteraction
            .addCase(createCustomerInteraction.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createCustomerInteraction.fulfilled, (state, action) => {
                state.interactionDetails = action.payload;
                state.error = null;
                state.isCreating = false;
            })
            .addCase(createCustomerInteraction.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nella creazione dell'interazione.";
                state.isCreating = false;
            })
            // updateCustomerInteraction
            .addCase(updateCustomerInteraction.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateCustomerInteraction.fulfilled, (state, action) => {
                state.interactionDetails = action.payload;
                state.error = null;
                state.isUpdating = false;
            })
            .addCase(updateCustomerInteraction.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nell'aggiornamento dell'interazione.";
                state.isUpdating = false;
            })
            // deleteCustomerInteraction
            .addCase(deleteCustomerInteraction.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteCustomerInteraction.fulfilled, (state) => {
                state.interactionDetails = null;
                state.error = null;
                state.isDeleting = false;
            })
            .addCase(deleteCustomerInteraction.rejected, (state, action) => {
                state.error = action.payload?.message || "Errore sconosciuto nell'eliminazione dell'interazione.";
                state.isDeleting = false;
            })
    }
});

// Selectors
export const selectCustomerInteractionsData = (state) => state.customerInteractions.data;
export const selectCustomerInteractionDetails = (state) => state.customerInteractions.interactionDetails;
export const selectCustomerInteractionsLoading = (state) => state.customerInteractions.isLoading;
export const selectCustomerInteractionCreating = (state) => state.customerInteractions.isCreating;
export const selectCustomerInteractionUpdating = (state) => state.customerInteractions.isUpdating;
export const selectCustomerInteractionDeleting = (state) => state.customerInteractions.isDeleting;
export const selectCustomerInteractionsError = (state) => state.customerInteractions.error;

export const { clearCustomerInteractionsData } = customerInteractionsSlice.actions;
export default customerInteractionsSlice.reducer;
